<template>
    <div class="relative">
        <h1 class="text-lg font-medium text-iron-700">
            {{ $t("event_types.booking_form.title") }}
        </h1>
        <p class="text-sm text-iron-500">
            {{ $t("event_types.booking_form.description") }}
        </p>

        <div class="right-0 top-0 absolute">
            <button @click="copyAsCode">
                <Code v-if="!copied" :size="12"></Code
                ><ClipboardCopy v-if="copied" :size="12"></ClipboardCopy>
            </button>
        </div>

        <p class="mt-7 text-sm font-medium text-iron-700">
            {{ $t("event_types.booking_form.questions_label") }}
        </p>
        <div>
            <VueDraggable
                ref="el"
                v-model="elements"
                :animation="150"
                target=".sort-target"
                handle=".field-card-handle"
                ghost-class="ghost"
                @end="onEnd"
                @start="onStart"
            >
                <TransitionGroup
                    type="transition"
                    tag="ul"
                    :name="!drag ? 'fade' : undefined"
                    class="sort-target"
                >
                    <li v-for="element of elements" :key="element.uuid">
                        <FormElement
                            :id="element.id"
                            class="my-3"
                            :label="element.label"
                            :name="element.name"
                            :options="element.options"
                            :merge-tag="element.merge_tag"
                            :type="element.type"
                            :required="element.required"
                            :include-in-integration="
                                element.include_in_integration ?? true
                            "
                            :is-new="element.isNew"
                            :uuid="element.uuid"
                            :event-type-id="props.eventType.id"
                            @remove-locally="removeField(element.id)"
                            @on-success="onSuccess"
                            @on-new-added="onSuccess"
                        />
                    </li>
                </TransitionGroup>
            </VueDraggable>
        </div>

        <div class="mt-4">
            <AddFormElement
                :has-firstname="hasFirstName"
                :has-lastname="hasLastName"
                :has-phone="hasPhone"
                @add-custom-field="addField"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, nextTick, computed } from "vue";
import { CustomField, EventType } from "../../types/EventType";
import FormElement from "./components/FormElement.vue";
import { VueDraggable } from "vue-draggable-plus";
import route from "ziggy-js";
import { SortableEvent } from "sortablejs";
import AddFormElement, { type Question } from "./components/AddFormElement.vue";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { useClipboard } from "@vueuse/core";
import { Code, ClipboardCopy } from "lucide-vue-next";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    eventType: EventType;
}>();

const elements = ref(props.eventType.customFields);

const { copy, copied } = useClipboard();
const { t } = useI18n();

const drag = ref(false);

const hasFirstName = computed(() => {
    return elements.value.some(
        (element: CustomField) => element.merge_tag === "FIRST_NAME",
    );
});

const hasLastName = computed(() => {
    return elements.value.some(
        (element: CustomField) => element.merge_tag === "LAST_NAME",
    );
});

const hasPhone = computed(() => {
    return elements.value.some(
        (element: CustomField) => element.merge_tag === "PHONE",
    );
});

function removeField(id: string | number) {
    elements.value = elements.value.filter((element: CustomField) => {
        return element.id !== id;
    });
}

function onSuccess({
    newCustomField,
    oldUuid,
}: {
    newCustomField: CustomField;
    oldUuid: string | number;
}) {
    elements.value = elements.value.map((element: CustomField) => {
        if (element.id === oldUuid) {
            element = newCustomField;
        }

        return element;
    });
}

function addField(question: Question) {
    console.log(question);

    let mergeTag = "";
    let label = "";

    if (question.predefined === "first_name") {
        mergeTag = "FIRST_NAME";
        label = t("event_types.booking_form.questions.first_name.title");
    }

    if (question.predefined === "last_name") {
        mergeTag = "LAST_NAME";
        label = t("event_types.booking_form.questions.last_name.title");
    }

    if (question.predefined === "phone") {
        mergeTag = "PHONE";
        label = t("event_types.booking_form.questions.phone.title");
    }

    //@ts-expect-error
    elements.value.push({
        id: 123, // temporary id
        uuid: uuidv4(),
        name: "",
        label: label,
        merge_tag: mergeTag,
        required: false,
        include_in_integration: true,
        type: question.type,
        isNew: true,
    });
}

function onStart() {
    drag.value = true;
}
function onEnd(e: SortableEvent) {
    if (e.oldIndex === e.newIndex) {
        return;
    }

    if (e.oldIndex === undefined) {
        return;
    }

    const movedElement = props.eventType.customFields[e.oldIndex];

    axios.put(
        route("app.event-types.custom-fields.update", {
            eventType: props.eventType.id,
            customField: movedElement.id,
        }),
        {
            name: movedElement.name,
            label: movedElement.label,
            mergeTag: movedElement.merge_tag,
            required: movedElement.required,
            order: e.newIndex,
        },
    );

    nextTick(() => {
        drag.value = false;
    });
}

function copyAsCode() {
    let stringToCopy = "const map = new Map();\n";

    const knownFields = ["EMAIL", "FIRST_NAME", "LAST_NAME", "PHONE", "SEATS"];

    for (const field of props.eventType.customFields) {
        if (!knownFields.includes(field.merge_tag)) {
            stringToCopy += `map.set('${field.merge_tag.toLocaleLowerCase()}', '${
                field.uuid
            }');\n`;
        }
    }

    copy(stringToCopy);
}
</script>

<style>
.fade-move,
.fade-enter-active,
.fade-leave-active {
    transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: scaleY(0.01) translate(30px, 0);
}

.fade-leave-active {
    position: absolute;
}

.ghost {
    opacity: 0.5;
    background: #ebebeb !important;
}
</style>
